import React, { useEffect, useState } from 'react';
import { Ban, CheckCircle, Loader2, RefreshCw, Trash2, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { BACKEND_URL } from '../../config';

const NegativeListSection = () => {
  const [negativeList, setNegativeList] = useState([]);
  const [loadingNegativeList, setLoadingNegativeList] = useState(false);
  const [negativeListCsvFile, setNegativeListCsvFile] = useState(null);
  const [uploadingNegativeList, setUploadingNegativeList] = useState(false);
  const [negativeListSearchTerm, setNegativeListSearchTerm] = useState('');

  const fetchNegativeList = async () => {
    setLoadingNegativeList(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/negative-list`);
      const data = await response.json();
      if (data.status === 'success') setNegativeList(data.schemes || []);
    } catch (error) {
      console.error('Error fetching negative list:', error);
      toast.error('Failed to load negative list');
    } finally {
      setLoadingNegativeList(false);
    }
  };

  const handleUploadNegativeList = async () => {
    if (!negativeListCsvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setUploadingNegativeList(true);
    try {
      const formData = new FormData();
      formData.append('file', negativeListCsvFile);
      const response = await fetch(`${BACKEND_URL}/api/negative-list/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Negative list uploaded successfully');
        setNegativeListCsvFile(null);
        fetchNegativeList();
      } else {
        toast.error(data.detail || 'Failed to upload negative list');
      }
    } catch (error) {
      console.error('Error uploading negative list:', error);
      toast.error('Failed to upload negative list');
    } finally {
      setUploadingNegativeList(false);
    }
  };

  const handleRemoveFromNegativeList = async (amc_name, scheme_name) => {
    try {
      const encodedAmc = encodeURIComponent(amc_name);
      const encodedScheme = encodeURIComponent(scheme_name);
      const response = await fetch(`${BACKEND_URL}/api/negative-list/${encodedAmc}/${encodedScheme}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Scheme removed from negative list');
        fetchNegativeList();
      } else {
        toast.error(data.detail || 'Failed to remove scheme');
      }
    } catch (error) {
      console.error('Error removing from negative list:', error);
      toast.error('Failed to remove scheme');
    }
  };

  const handleClearNegativeList = async () => {
    if (!window.confirm('Are you sure you want to clear the entire negative list? This action cannot be undone.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/negative-list/clear`, { method: 'DELETE' });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Cleared ${data.deleted} schemes from negative list`);
        fetchNegativeList();
      } else {
        toast.error(data.detail || 'Failed to clear negative list');
      }
    } catch (error) {
      console.error('Error clearing negative list:', error);
      toast.error('Failed to clear negative list');
    }
  };

  useEffect(() => {
    fetchNegativeList();
  }, []);

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-2 border-red-200">
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b-2 border-red-200">
          <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
            <Ban className="w-6 h-6 text-red-600" />
            Negative List - Hide Schemes
          </CardTitle>
          <CardDescription>
            Upload a CSV to hide specific schemes from the landing page. Other schemes from the same AMC will still be visible.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <div className="border-2 border-dashed border-red-300 rounded-lg p-6 bg-red-50">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-lg font-semibold text-red-900">Upload Negative List CSV</h3>
              <p className="mt-1 text-sm text-red-700">
                CSV should have columns: <code className="bg-red-100 px-1 rounded">amc_name</code> and <code className="bg-red-100 px-1 rounded">scheme_name</code>
              </p>
              <div className="mt-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setNegativeListCsvFile(e.target.files[0])}
                  className="max-w-xs mx-auto"
                />
              </div>
              {negativeListCsvFile && (
                <p className="mt-2 text-sm text-red-800">Selected: {negativeListCsvFile.name}</p>
              )}
              <Button
                onClick={handleUploadNegativeList}
                disabled={!negativeListCsvFile || uploadingNegativeList}
                className="mt-4 bg-red-600 hover:bg-red-700"
              >
                {uploadingNegativeList ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload & Add to Negative List</>
                )}
              </Button>
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Current Negative List ({negativeList.length} schemes)
              </h3>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchNegativeList} disabled={loadingNegativeList}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingNegativeList ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {negativeList.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={handleClearNegativeList}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>

            {negativeList.length > 0 && (
              <div className="mb-4">
                <Input
                  placeholder="Search by AMC or Scheme name..."
                  value={negativeListSearchTerm}
                  onChange={(e) => setNegativeListSearchTerm(e.target.value)}
                  className="max-w-md"
                />
              </div>
            )}

            {loadingNegativeList ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-red-500" />
                <p className="mt-2 text-slate-600">Loading negative list...</p>
              </div>
            ) : negativeList.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Ban className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p>No schemes in negative list</p>
                <p className="text-sm">Upload a CSV to hide specific schemes from the landing page</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-semibold text-slate-700">AMC Name</th>
                      <th className="text-left p-2 font-semibold text-slate-700">Scheme Name</th>
                      <th className="text-left p-2 font-semibold text-slate-700">Added</th>
                      <th className="text-center p-2 font-semibold text-slate-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {negativeList
                      .filter((item) =>
                        !negativeListSearchTerm ||
                        item.amc_name?.toLowerCase().includes(negativeListSearchTerm.toLowerCase()) ||
                        item.scheme_name?.toLowerCase().includes(negativeListSearchTerm.toLowerCase())
                      )
                      .map((item, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-red-50">
                          <td className="p-2 text-slate-800">{item.amc_name}</td>
                          <td className="p-2 text-slate-800">{item.scheme_name}</td>
                          <td className="p-2 text-slate-500 text-xs">
                            {item.added_at ? new Date(item.added_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFromNegativeList(item.amc_name, item.scheme_name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-100"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              How It Works
            </h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Upload a CSV with AMC Name and Scheme Name columns</li>
              <li>Schemes in the negative list will be hidden from the landing page</li>
              <li>Other schemes from the same AMC will still be visible</li>
              <li>Duplicate entries are automatically skipped</li>
              <li>Click the X button to remove individual schemes</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NegativeListSection;
